import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useRef } from 'react';
import sanitizeHtml from 'sanitize-html';
import EditIcon from '@mui/icons-material/Edit';

import './DocumentResultCard.css';
import { Link } from 'react-router-dom';
import { useAppContext } from '../Store';
import { SET_PREVIOUS_VIEWDOC_ID, SHOW_RESULT_CARD_DETAILS } from '../reducer';
import { Stack } from '@mui/material';
import ResultCardDetails from './ResultCardDetails';
import { sortHitList } from '../utils/helper';
import useHighlightCard from '../hooks/useHighlightCard';

var HtmlToReactParser = require('html-to-react').Parser;

type PropsType = {
  doc: any;
  isEditor: boolean;
  disableCardAnchor: boolean;
  isArchive?: boolean;
};

const highlightedBorderStyle = {
  border: '2px solid',
  borderRadius: '8px',
  borderColor: 'green',
};

const DocumentResultCard = ({ doc, isEditor, disableCardAnchor, isArchive }: PropsType) => {
  const { state, dispatch } = useAppContext();
  const { search } = state;
  const { expandedIds } = search;
  const { highlight, _source } = doc;
  let highlightElement;
  const headerRef = useRef<null | HTMLDivElement>(null);
  const { highlightHeader } = useHighlightCard(headerRef, doc._id, disableCardAnchor);

  if (!_source) return null;

  try {
    let highlights =
      highlight && highlight['attachment.content'] && highlight['attachment.content'].join('<br/>');

    if (!highlights) {
      highlights = _source.attachment && _source.attachment.content;
    }

    const cleanHighlights = sanitizeHtml(highlights.replace(/(\r\n|\n|\r)/gm, ''), {
      allowedTags: ['strong', 'br'],
    });
    const htmlToReactParser = new HtmlToReactParser();

    highlightElement = htmlToReactParser.parse(cleanHighlights);
  } catch (error) {
    console.error(error);
  }

  return (
    <Box
      className="listing-card-row-seperator "
      display={'flex'}
      flexDirection={'column'}
      id={doc._id}
      ref={headerRef}
    >
      <Stack
        className=" margin-bottom-0 listing-card-row"
        width={'100%'}
        display={'flex'}
        alignItems={'flex-start'}
        direction={'row'}
        onClick={() => {
          dispatch({
            type: SHOW_RESULT_CARD_DETAILS,
            payload: { id: doc._id },
          });
        }}
        sx={{
          '&:hover': {
            borderColor: highlightHeader ? 'green' : '#dbdbdb',
          },
          ...(highlightHeader && { ...highlightedBorderStyle }),
        }}
      >
        <Stack width={'20%'}>
          <Typography className="cell">
            <Tooltip title={_source.form_status === 'Active' ? 'Active' : 'Inactive'}>
              <span
                className="tag--primary"
                style={{
                  background: _source.form_status === 'Active' ? 'green' : 'red',
                }}
              ></span>
            </Tooltip>
            <strong>{_source.form_number}</strong>
          </Typography>
        </Stack>
        <Stack width={'60%'}>
          <Typography maxWidth={'90%'}>
            <strong>{_source.form_title}</strong> <br />
            <Typography
              sx={{
                paddingTop: '4px',
                fontSize: 14,
                textWrap: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'pre',
              }}
            >
              {highlightElement}
            </Typography>
          </Typography>
        </Stack>
        <Typography width={'20%'}>
          <strong>{_source.form_type}</strong>
        </Typography>
      </Stack>
      {expandedIds?.[doc._id] && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            marginInline: '1rem',
            backgroundColor: '#FAFAFA',
            border: '1px solid #dbdbdb',
            borderTop: 0,
            borderBottomRightRadius: '6px',
            borderBottomLeftRadius: '6px',
          }}
        >
          {sortHitList(doc.inner_hits.group_by_form_number.hits.hits).map(
            (h: any, index: number, arr: any) => (
              <ResultCardDetails
                key={h._id}
                isArchive={isArchive}
                h={h}
                lastResult={index + 1 === arr.length}
                disableCardAnchor={disableCardAnchor}
                headerRef={headerRef}
              />
            )
          )}
        </div>
      )}
      {isEditor && (
        <Box component="span" className="tag--actions" display={'flex'}>
          <Tooltip title={`Edit ${_source.form_number ?? 'form'}`}>
            <Link
              to={`/forms/${doc._id}`}
              onClick={() =>
                dispatch({ type: SET_PREVIOUS_VIEWDOC_ID, payload: doc._id.concat('|from-edit') })
              }
            >
              <IconButton
                size="small"
                aria-label="edit"
                sx={{ height: '34px', alignSelf: 'center' }}
              >
                <EditIcon />
              </IconButton>
            </Link>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default DocumentResultCard;
